import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const SERVICES = [
  { to: "/servicios/aeropuerto-y-tours", label: "Aeropuerto y Tours" },
  { to: "/servicios/transporte-turistico-colombia", label: "Transporte por Colombia" },
  { to: "/servicios/rutas", label: "Rutas Empresariales / Escolares / Nocturnas" },
  { to: "/servicios/transporte-eventos-congresos", label: "Eventos y Congresos" },
  { to: "/servicios/aliados-airbnb", label: "Aliados Airbnb (10% de descuento)" },
];

const NAV = [
  { to: "/", label: "Inicio", end: true },
  { label: "Servicios", isDropdown: true },
  { to: "/nosotros", label: "Nosotros" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "Preguntas Frecuentes" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servOpen, setServOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServOpen(false); }, [pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm whitespace-nowrap ${isActive ? "text-[#E8C87A]" : "text-[#F5EFE0]/80 hover:text-[#E8C87A]"}`;

  const serviceActive = pathname.startsWith("/servicios/");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b ${scrolled ? "bg-[#0A0908]/90 backdrop-blur-md border-[#D4AF37]/25" : "bg-[#0A0908]/60 backdrop-blur border-transparent"}`}
      style={{ transitionProperty: "background-color,border-color", transitionDuration: "300ms" }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[74px] flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img src="/logo-lineas-doradas.webp" alt="Líneas Doradas S.A.S."
               className="h-14 w-14 object-contain shrink-0 group-hover:scale-105"
               style={{ transitionProperty: "transform", transitionDuration: "300ms" }} />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[17px] text-[#F5EFE0] tracking-wide">Líneas Doradas S.A.S.</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Transporte Turístico por toda Colombia</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
          {NAV.map((n) => {
            if (n.isDropdown) {
              return (
                <div key="services" ref={dropdownRef} className="relative">
                  <button
                    data-testid="nav-services-btn"
                    onClick={() => setServOpen((o) => !o)}
                    className={`flex items-center gap-1 text-sm ${serviceActive ? "text-[#E8C87A]" : "text-[#F5EFE0]/80 hover:text-[#E8C87A]"}`}
                  >
                    Servicios <ChevronDown size={14} className={servOpen ? "rotate-180" : ""} style={{ transitionProperty: "transform", transitionDuration: "200ms" }} />
                  </button>
                  {servOpen && (
                    <div className="absolute right-0 mt-3 w-72 bg-[#171412] border border-[#D4AF37]/30 rounded-xl shadow-xl overflow-hidden">
                      {SERVICES.map((s) => (
                        <Link key={s.to} to={s.to} data-testid={`dropdown-${s.to}`}
                              className="block px-4 py-3 text-sm text-[#F5EFE0]/85 hover:bg-[#D4AF37]/10 hover:text-[#E8C87A] border-b border-[#3A3229] last:border-b-0">
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink key={n.to} to={n.to} end={n.end}
                       data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                       className={linkCls}>
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/cotizar"
            data-testid="header-cta-cotizar"
            className="hidden md:inline-flex btn-gold px-5 py-2.5 rounded-full text-sm"
          >
            Cotizar
          </Link>
          <button
            data-testid="mobile-nav-toggle"
            aria-label="Menú"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-[#E8C87A] p-2 rounded-md border border-[#D4AF37]/30"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A0908] border-t border-[#D4AF37]/25 px-5 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-nav">
          <Link to="/" className="py-2.5 text-[#F5EFE0]/90 border-b border-[#3A3229]">Inicio</Link>
          <div className="py-2 text-[10px] uppercase tracking-widest text-[#D4AF37]">Servicios</div>
          {SERVICES.map((s) => (
            <Link key={s.to} to={s.to} className="pl-3 py-2 text-[#F5EFE0]/80 border-b border-[#3A3229] text-sm">{s.label}</Link>
          ))}
          <Link to="/nosotros" className="py-2.5 text-[#F5EFE0]/90 border-b border-[#3A3229]">Nosotros</Link>
          <Link to="/blog" className="py-2.5 text-[#F5EFE0]/90 border-b border-[#3A3229]">Blog</Link>
          <Link to="/faq" className="py-2.5 text-[#F5EFE0]/90 border-b border-[#3A3229]">Preguntas Frecuentes</Link>
          <Link to="/contacto" className="py-2.5 text-[#F5EFE0]/90 border-b border-[#3A3229]">Contacto</Link>
          <Link to="/cotizar" className="btn-gold mt-3 py-3 rounded-full text-sm text-center">Cotizar</Link>
        </div>
      )}
    </header>
  );
}
